import React, { memo, useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import cx from 'clsx';

import { useAppDispatch } from '../../../../redux/hooks/app-hooks';
import { setActiveAnswer } from '../../../../redux/slices/answers/answers-slice';
import { getActiveElement } from '../../../../utils/active-element';

import styles from '../Answer.module.scss';

export type AnswerWrapperProps = {
    answerUuid: string;
    children: React.ReactNode;
    isActive?: boolean;
    isGrayedOut?: boolean;
    className?: string;
    onClick?: React.MouseEventHandler<HTMLDivElement>;
    onMouseOver?: React.MouseEventHandler<HTMLDivElement>;
    dataTestId?: string;
    disableManuallyTrackingFocus?: boolean;
};

const AnswerWrapperComponent: React.FC<AnswerWrapperProps> = (props) => {
    const ref = useRef<HTMLDivElement>(null);
    const dispatch = useAppDispatch();
    const { answerUuid, dataTestId, isActive, isGrayedOut, disableManuallyTrackingFocus, className, ...elementProps } =
        props;

    const handleOnFocus = useCallback(() => {
        dispatch(setActiveAnswer(answerUuid));
    }, [answerUuid, dispatch]);

    const handleOnBlur = useCallback(() => {
        if (isActive) {
            dispatch(setActiveAnswer(''));
        }
    }, [dispatch, isActive]);

    const manualFocusTracking = useMemo(() => {
        if (disableManuallyTrackingFocus) {
            return undefined;
        }

        return {
            onFocus: handleOnFocus,
            onBlur: handleOnBlur,
        };
    }, [disableManuallyTrackingFocus, handleOnFocus, handleOnBlur]);

    useLayoutEffect(() => {
        if (disableManuallyTrackingFocus) {
            return;
        }

        if (ref.current && isActive && getActiveElement() !== ref.current) {
            ref.current.focus({ preventScroll: true });
            ref.current.scrollIntoView({ block: 'center' });
        }

        return () => {
            dispatch(setActiveAnswer(''));
        };
    }, [isActive, answerUuid, dispatch, disableManuallyTrackingFocus]);

    return (
        <div
            ref={ref}
            {...elementProps}
            {...manualFocusTracking}
            className={cx(styles.wrapper, className, isGrayedOut && styles.gray)}
            tabIndex={disableManuallyTrackingFocus ? undefined : -1}
            data-testid={dataTestId}
        />
    );
};

export const AnswerWrapper = memo(AnswerWrapperComponent);
